import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Clock, AlertCircle, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyUniformsPage() {
  const [uniforms, setUniforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentInfo, setStudentInfo] = useState({
    name: '',
    studentId: '',
    grade: ''
  });

  useEffect(() => {
    const fetchMyUniforms = async () => {
      try {
        setLoading(true);
        const studentId = localStorage.getItem('userId');
        
        const response = await fetch(`${API}/uniforms/student/${studentId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUniforms(data.uniforms || []);
          setStudentInfo(data.studentInfo || {});
        }
      } catch (error) {
        console.error('Error fetching uniforms:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMyUniforms();
  }, []);

  const collectedCount = uniforms.filter(u => u.status === 'Collected').length;
  const pendingCount = uniforms.filter(u => u.status === 'Pending').length;
  const completionPercentage = uniforms.length > 0 ? Math.round((collectedCount / uniforms.length) * 100) : 0;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Collected':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'Not Available':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Collected':
        return 'bg-green-50 border-green-200';
      case 'Pending':
        return 'bg-yellow-50 border-yellow-200';
      case 'Not Available':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Collected':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Not Available':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading your uniforms...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">My Uniforms</h1>
        <p className="text-gray-600 mt-1">
          {studentInfo.name} • Grade {studentInfo.grade}
        </p>
      </div>

      {/* Progress Card */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Uniform Collection Progress</p>
              <p className="text-3xl font-bold text-gray-900">{completionPercentage}%</p>
              <p className="text-sm text-gray-600 mt-1">
                {collectedCount} of {uniforms.length} items collected
              </p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-green-600">{collectedCount}</p>
              <p className="text-sm text-gray-600">Collected</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
            <div
              className="bg-gradient-to-r from-green-500 to-blue-600 h-full rounded-full transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>

          {/* Status Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{collectedCount}</p>
              <p className="text-xs text-gray-600">✓ Collected</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
              <p className="text-xs text-gray-600">⏱ Pending</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-400">
                {uniforms.filter(u => u.status === 'Not Available').length}
              </p>
              <p className="text-xs text-gray-600">⚠ Unavailable</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Uniform Items */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Required Uniforms</h2>
        
        {uniforms.map((uniform) => (
          <Card
            key={uniform.id}
            className={`p-4 border-l-4 border-l-blue-500 ${getStatusColor(uniform.status)} transition-all`}
          >
            <div className="flex items-start gap-4">
              {/* Status Icon */}
              <div className="flex-shrink-0 mt-1">
                {getStatusIcon(uniform.status)}
              </div>

              {/* Item Details */}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{uniform.itemName}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Category: <span className="font-medium">{uniform.category || 'General'}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(uniform.status)}`}>
                      {uniform.status}
                    </span>
                  </div>
                </div>

                {/* Quantity & Details */}
                <div className="mt-3 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Quantity Required</p>
                    <p className="text-lg font-semibold text-gray-900">{uniform.quantityRequired}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Size</p>
                    <p className="text-lg font-semibold text-gray-900">{uniform.size || 'N/A'}</p>
                  </div>
                  {uniform.status === 'Collected' && uniform.dateCollected && (
                    <div>
                      <p className="text-xs text-gray-600">Date Collected</p>
                      <p className="text-lg font-semibold text-green-600">
                        {new Date(uniform.dateCollected).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Collected By */}
                {uniform.status === 'Collected' && uniform.collectedBy && (
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <p className="text-xs text-gray-600">
                      Collected by <span className="font-medium">{uniform.collectedBy}</span>
                    </p>
                  </div>
                )}

                {/* Receipt/Proof */}
                {uniform.receiptFile && (
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <a
                      href={uniform.receiptFile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Download Collection Receipt
                    </a>
                  </div>
                )}

                {/* Reason for Not Available */}
                {uniform.status === 'Not Available' && uniform.reason && (
                  <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-lg">
                    <p className="text-sm text-red-800">
                      <strong>Reason:</strong> {uniform.reason}
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      Please contact the office for more information.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}

        {uniforms.length === 0 && (
          <Card className="p-8 text-center bg-gray-50">
            <p className="text-gray-500">No uniform requirements found for your grade.</p>
          </Card>
        )}
      </div>

      {/* All Collected Message */}
      {uniforms.length > 0 && completionPercentage === 100 && (
        <Card className="p-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200 border-2">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-green-900">All Uniforms Collected! 🎉</h3>
            <p className="text-green-700 mt-2">
              You have successfully collected all required uniforms for this term.
            </p>
            <Button className="mt-4 bg-green-600 hover:bg-green-700">
              <Download className="w-4 h-4 mr-2" /> Download Complete Receipt
            </Button>
          </div>
        </Card>
      )}

      {/* Download Summary */}
      <div className="flex gap-2 justify-center">
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Download className="w-4 h-4 mr-2" /> Download Summary
        </Button>
        <Button className="bg-gray-600 hover:bg-gray-700">
          📧 Email Summary
        </Button>
      </div>

      {/* Contact Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <p className="text-sm text-gray-700">
          <strong>Questions about your uniforms?</strong> Contact the office at
          <a href="tel:+1234567890" className="text-blue-600 hover:underline ml-1">+1 (234) 567-890</a> or
          <a href="mailto:admin@school.local" className="text-blue-600 hover:underline ml-1">admin@school.local</a>
        </p>
      </Card>
    </div>
  );
}
